import type { JSX } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@feathr/components';

// Max number of sample email addresses rendered inline in the audience description.
const SAMPLE_EMAIL_DISPLAY_COUNT = 2;

interface IAudienceDescriptionProps {
  /** Total number of unique email recipients in the audience. */
  audienceCount: number;
  /** Up to SAMPLE_EMAIL_DISPLAY_COUNT example recipients rendered inline. */
  sampleEmails: string[];
}

function AudienceDescription({
  audienceCount,
  sampleEmails,
}: Readonly<IAudienceDescriptionProps>): JSX.Element {
  const { t } = useTranslation();
  const formattedCount = audienceCount.toLocaleString();
  const strongComponent = { strong: <strong /> };

  let content: JSX.Element;
  if (
    sampleEmails.length >= SAMPLE_EMAIL_DISPLAY_COUNT &&
    audienceCount === SAMPLE_EMAIL_DISPLAY_COUNT
  ) {
    content = (
      <Trans
        components={strongComponent}
        i18nKey={'audience-description-with-both-samples'}
        t={t}
        values={{ formattedCount, email1: sampleEmails[0], email2: sampleEmails[1] }}
      >
        {
          "Currently, you're about to email <strong>{{formattedCount}} people</strong> including {{email1}} and {{email2}}."
        }
      </Trans>
    );
  } else if (
    sampleEmails.length >= SAMPLE_EMAIL_DISPLAY_COUNT &&
    audienceCount > SAMPLE_EMAIL_DISPLAY_COUNT
  ) {
    const remaining = audienceCount - SAMPLE_EMAIL_DISPLAY_COUNT;
    content = (
      <Trans
        components={strongComponent}
        count={remaining}
        i18nKey={'audience-description-with-two-samples'}
        t={t}
        values={{
          formattedCount,
          email1: sampleEmails[0],
          email2: sampleEmails[1],
          formattedRemaining: remaining.toLocaleString(),
        }}
      >
        {
          "Currently, you're about to email <strong>{{formattedCount}} people</strong> including {{email1}}, {{email2}}, and {{formattedRemaining}} others."
        }
      </Trans>
    );
  } else if (sampleEmails.length === 1) {
    content = (
      <Trans
        components={strongComponent}
        count={audienceCount}
        i18nKey={'audience-description-with-one-sample'}
        t={t}
        values={{ formattedCount, email1: sampleEmails[0] }}
      >
        {
          "Currently, you're about to email <strong>{{formattedCount}} people</strong> including {{email1}}."
        }
      </Trans>
    );
  } else {
    content = (
      <Trans
        components={strongComponent}
        count={audienceCount}
        i18nKey={'audience-description-count-only'}
        t={t}
        values={{ formattedCount }}
      >
        {"Currently, you're about to email <strong>{{formattedCount}} people</strong>."}
      </Trans>
    );
  }

  return <Text.DefaultRegular truncate={false}>{content}</Text.DefaultRegular>;
}

export default AudienceDescription;
